import {
  FORMATION_GROUPS_3_AT_THE_BACK,
  FORMATION_GROUPS_4_AT_THE_BACK,
  FORMATION_GROUPS_5_AT_THE_BACK,
} from "@/features/Tactics/Sidebars/TacticFiltersSidebar";
import { positionToChain } from "@/functions/getFormation";
import {
  SelectedFilters,
  TacticSortOption,
  TacticSortOptionValues,
} from "@/redux/tactics/tacticsSlice";

export const DEFAULT_TACTIC_SORT: TacticSortOptionValues = "recommended";

const SORT_VALUES: TacticSortOptionValues[] = [
  "recommended",
  "date-added",
  "popularity-score",
];

const FORMATION_GROUP_VALUES = [
  FORMATION_GROUPS_3_AT_THE_BACK,
  FORMATION_GROUPS_4_AT_THE_BACK,
  FORMATION_GROUPS_5_AT_THE_BACK,
];

// The filters sidebar offers every outfield position.
const POSITION_VALUES = Object.keys(positionToChain).filter(
  (position) => position !== "GK",
);

const FILTER_PARAMS = {
  author: "author",
  formation: "formation",
  formationGroup: "formationGroup",
  positions: "positions",
} as const;

const SORT_PARAM = "sort";

export type TacticsUrlState = {
  selectedFilters: SelectedFilters;
  sort: TacticSortOption;
};

// Labels are resolved where they are known (formations come from loaded
// content, formation groups are translated); everything else labels itself.
export type TacticFilterLabels = {
  formation: (value: string) => string;
  formationGroup: (value: string) => string;
};

const readValues = (params: URLSearchParams, key: string) =>
  (params.get(key) ?? "")
    .split(",")
    .map((value) => value.trim())
    .filter(Boolean);

const upperCased = (values: string[]) =>
  values.map((value) => value.toUpperCase());

export const searchParamsToTacticsState = (
  params: URLSearchParams,
  labels: TacticFilterLabels,
): TacticsUrlState => {
  const toFilters = (
    category: keyof SelectedFilters,
    label: (value: string) => string,
    isValid: (value: string) => boolean = () => true,
    normalize: (values: string[]) => string[] = (values) => values,
  ) =>
    normalize(readValues(params, FILTER_PARAMS[category]))
      .filter(isValid)
      .map((value) => ({ label: label(value), value, category }));

  const sortParam = params.get(SORT_PARAM) as TacticSortOptionValues | null;

  return {
    selectedFilters: {
      author: toFilters("author", (value) => value),
      formation: toFilters("formation", labels.formation),
      formationGroup: toFilters(
        "formationGroup",
        labels.formationGroup,
        (value) => FORMATION_GROUP_VALUES.includes(value),
      ),
      positions: toFilters(
        "positions",
        (value) => value,
        (value) => POSITION_VALUES.includes(value),
        upperCased,
      ),
    },
    sort: {
      value:
        sortParam && SORT_VALUES.includes(sortParam)
          ? sortParam
          : DEFAULT_TACTIC_SORT,
    },
  };
};

export const tacticsStateToSearchParams = (
  current: URLSearchParams,
  { selectedFilters, sort }: TacticsUrlState,
): URLSearchParams => {
  const next = new URLSearchParams(current);
  Object.values(FILTER_PARAMS).forEach((param) => next.delete(param));
  next.delete(SORT_PARAM);

  (Object.keys(FILTER_PARAMS) as (keyof SelectedFilters)[]).forEach(
    (category) => {
      const filters = selectedFilters[category];
      if (filters.length > 0) {
        next.set(
          FILTER_PARAMS[category],
          filters.map((filter) => filter.value).join(","),
        );
      }
    },
  );

  if (sort.value !== DEFAULT_TACTIC_SORT) {
    next.set(SORT_PARAM, sort.value);
  }

  return next;
};
